<script>
	const Vue = require('vue');
	const uaParser = new (require('ua-parser-js'))();
	const _ = require('underscore');
	const analyticsUtilities = require('utilities/analyticsUtilities');
	const round = require('utilities/numberUtilities').round;
	const $ = require('jquery');
	const logger = require('utilities/logger');

	const fireEvent = analyticsUtilities.fireEvent;
	const AUDIO_PLAY = analyticsUtilities.events.AUDIO_PLAY;
	const AUDIO_PAUSE = analyticsUtilities.events.AUDIO_PAUSE;
	const AUDIO_COMPLETE = analyticsUtilities.events.AUDIO_COMPLETE;
	const AUDIO_SPEED_CHANGE = analyticsUtilities.events.AUDIO_SPEED_CHANGE;

	let retryCurrentTimeFunc = null;

	let throttleSpacebar = false;
	let browser = uaParser.getBrowser();

	if (browser && (browser.name === 'IE' || browser.name === 'Edge' || browser.name === 'Firefox')) {
		throttleSpacebar = true;
	}

	module.exports = {
		components: {
			'dropdown-button': require('components/dropdown-button.vue').default
		},
		props: {
			src: {
				required: true
			},
			eventData: {
				default: {}
			},
			firePlayEvent: {
				default: true
			},
			firePauseEvent: {
				default: true
			},
			fireCompleteEvent: {
				default: true
			},
			audioPlayEventName: {
				default: AUDIO_PLAY
			},
			audioPauseEventName: {
				default: AUDIO_PAUSE
			},
			audioCompleteEventName: {
				default: AUDIO_COMPLETE
			},
			showSpeedSelector: {
				default: false
			}
		},
		data() {
			return {
				lastPlayTime: null,
				currentSpeed: 1.0,
				playingPromise: undefined,
				paused: true
			};
		},
		computed: {
			speeds() {
				let speeds = [
					{
						value: 0.75,
						text: 'Slower (.75x)'
					},
					{
						value: 1,
						default: true,
						text: 'Normal'
					},
					{
						value: 1.5,
						text: 'Faster (1.5x)'
					},
					{
						value: 2.0,
						text: 'Fastest (2x)'
					}
				];

				speeds = speeds.map(s => {
					let selected = s.value === this.currentSpeed;
					return _.assign({}, s, {
						selected
					});
				});

				return speeds;
			},
			speedText() {
				let text = 'Speed';
				let speeds = this.speeds;

				speeds.some(s => {
					if (s.selected && !s.default) {
						text = s.text;
						return true;
					}
					return false;
				});

				return text;
			}
		},
		watch: {
			src(val, oldVal) {
				if (val) {
					this.paused = true;
					const player = this.$refs.audio;
					// Changing src on an existing audio element
					// needs a load() call to request the file.
					Vue.nextTick(() => player.load());
				}
			}
		},
		beforeDestroy() {
			let player = this.$refs.audio;
			// WEB-989 - Vue's destroy leaves audio element playing
			if (player) {
				player.pause();
			}
		},
		methods: {
			setSpeed(speed) {
				let value = speed.value;
				this.currentSpeed = value;
				this.$refs.audio.playbackRate = value;
				this.fireEvent(AUDIO_SPEED_CHANGE, {
					value
				});
			},
			getPlayer() {
				return this.$refs.audio;
			},
			// Seeking while metadata isn't loaded causes
			// and InvalidStateException (seen on IE11)
			// Ensure metadata is loaded before setting current time.
			setCurrentTime(val) {
				let player = this.$refs.audio;

				if (!player) {
					return;
				}

				if (player.readyState && !isNaN(player.duration)) {
					player.currentTime = val;
				} else {
					logger.info('audioEl not ready');
					let playerHelper = $(player);
					if (retryCurrentTimeFunc) {
						playerHelper.off('loadedmetadata', retryCurrentTimeFunc);
						logger.info('removing previous currentTime retry');
					}
					retryCurrentTimeFunc = () => {
						logger.info('audioEl ready now, setting currentTime');
						player.currentTime = val;
						retryCurrentTimeFunc = null;
					};
					playerHelper.one('loadedmetadata', retryCurrentTimeFunc);
				}
			},
			play() {
				this.playingPromise = this.$refs.audio.play();

				if (window.readworks.config.rwtesting) {
					this.playingPromise.catch(() => {});
				}
			},
			onPlay() {
				let player = this.getPlayer();
				let firePlayEvent = this.firePlayEvent;

				if (firePlayEvent) {
					this.fireEvent(this.audioPlayEventName);
				}

				if (player && typeof player.currentTime === 'number') {
					this.lastPlayTime = player.currentTime;
				}

				this.paused = false;
				this.$emit('play', { player });
			},
			pause() {
				const playingPromise = this.playingPromise;

				if (playingPromise && !this.paused) {
					playingPromise.then(() => {
						this.$refs.audio.pause();
					});
				}
			},
			onPause() {
				let player = this.getPlayer();
				let duration = null;
				let currentTime = null;
				let firePauseEvent = this.firePauseEvent;

				if (player) {
					duration = player.duration;
					currentTime = player.currentTime;

					if (duration !== currentTime) {
						// Actually an end
						if (firePauseEvent) {
							this.fireEvent(this.audioPauseEventName);
						}

						this.lastPlayTime = null;
					}
				}

				this.paused = true;
				this.$emit('pause');
			},
			onEnded() {
				let fireCompleteEvent = this.fireCompleteEvent;

				if (fireCompleteEvent) {
					this.fireEvent(this.audioCompleteEventName);
				}
				this.lastPlayTime = null;
				this.paused = true;
				this.$emit('ended');
			},
			onKeyDown(event) {
				if (throttleSpacebar && event && event.keyCode === 32) {
					event.stopPropagation();
					event.preventDefault();
				}
			},
			onKeyUp(event) {
				if (throttleSpacebar && event && event.keyCode === 32) {
					event.stopPropagation();
					event.preventDefault();
					this.togglePlayPause();
				}
			},
			togglePlayPause: _.debounce(
				function () {
					let player = this.getPlayer();
					let isPaused = player.paused;

					if (isPaused) {
						player.play();
					} else {
						player.pause();
					}
				},
				500,
				true
			),
			fireEvent(name, extraData = {}) {
				let player = this.getPlayer();
				let filepath = this.src;
				let lastPlayTime = this.lastPlayTime;
				let eventData = _.assign({}, this.eventData, extraData);

				let length = null;
				let duration = null;
				let position = null;

				if (player) {
					length = player.duration;
					position = player.currentTime;

					if (typeof lastPlayTime === 'number') {
						duration = position - lastPlayTime;
					}

					duration = round(duration);
					length = round(length);
					position = round(position);

					let data = {
						length,
						position,
						filepath
					};

					if (duration) {
						data.duration = duration;
					}

					data = _.assign(data, eventData);

					fireEvent(name, data);
				}
			}
		}
	};
</script>
<style lang="scss">
	@import 'config/colors';
	@import 'config/variables';

	.audio-player {
		display: flex;
		margin: 1em 0 1em;

		audio {
			flex: 1;
			flex-basis: 100%;
			width: auto;
			min-width: 6.5rem;
		}

		.speeds {
			display: flex;
			align-items: center;
			position: relative;
			text-align: center;

			.speed:first-child {
				position: relative;

				&:before {
					content: 'speed:';
					position: absolute;
					right: 102%;
				}
			}

			a {
				color: black;
				margin: 0 0.5rem;
				line-height: 1;

				&:before,
				&:after {
					color: transparent;
					content: '(';
				}

				&:after {
					content: ')';
				}
			}

			a.selected {
				position: relative;
				font-weight: bold;

				&:before,
				&:after {
					color: black;
				}
			}
		}

		.dropdown-button {
			margin: 0 0.5rem;
			font-size: 1rem;
			line-height: 1.75rem;

			.main-button {
				padding: 0.5rem 1rem;
				border-radius: 1rem;
				border-color: $c-grey;
				background-color: $c-white;

				&.overlay {
					border-radius: 1rem 1rem 0 0;
				}

				&:hover {
					border-color: $c-blue;
				}
			}
		}
	}

	@media only screen and (min-width: $bp-width-med) {
		.audio-player {
			.dropdown-button {
				.main-button {
					padding: 0;
					width: 9rem;
				}
			}
		}
	}
</style>
<template>
	<div class="audio-player print-hidden">
		<audio
			ref="audio"
			preload="metadata"
			controls="controls"
			controlslist="nodownload"
			@play="onPlay"
			@pause="onPause"
			@ended="onEnded"
			@keydown="onKeyDown"
			@keyup="onKeyUp"
			@timeupdate="$emit('timeupdate')">
			Your browser does not support playing our audio.
			<source
				:src="src"
				type="audio/mp3" />
		</audio>
		<div
			class="speeds"
			v-if="showSpeedSelector && src">
			<dropdown-button
				@click-item="setSpeed"
				:items="speeds"
				:icon-cls="'clock fa-regular'"
				:title-text="speedText"
				:class="{
					active: currentSpeed !== 1
				}"></dropdown-button>
		</div>
	</div>
</template>
